import type { CSSProperties, ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight, Download, ExternalLink, FileText, FileX } from 'lucide-react'
import { cn } from '@/lib/cn'
import { artworkExt, artworkKind, downloadArtwork, fileSize, renderPdfFirstPage, type PdfPreview } from '@/lib/artwork'
import type { OrderItemArtwork } from '@/types'
import Modal from '@/components/ui/Modal'
import Button from '@/components/ui/Button'

// A light checkerboard behind the preview, so a transparent PNG's edges show
// the way they do in a design app instead of melting into a white panel.
const CHECKER: CSSProperties = {
  background: 'repeating-conic-gradient(#e2e8f0 0% 25%, #ffffff 0% 50%) 50% / 16px 16px',
}

/**
 * An order item's artwork as thumbnails; a click opens a preview with the
 * original to download. Images show as they are and PDFs (PDF-compatible AI
 * files too) as page 1. EPS and other files the browser cannot draw get a
 * type tile, and the preview still offers the download.
 *
 * Older orders carry a filename with no upload; those get a dashed tile that
 * opens nothing, since there is no file behind it.
 */
export default function ArtworkGallery({ files }: { files: OrderItemArtwork[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  if (files.length === 0) return null
  const many = files.length > 1

  return (
    <>
      <div className="mt-2 flex flex-wrap gap-2">
        {files.map((file, index) => {
          const name = file.name ?? 'Artwork file'
          const number = many && (
            <span className="absolute left-1 top-1 flex size-5 items-center justify-center rounded-full bg-foreground/80 text-2xs font-bold text-white">
              {index + 1}
            </span>
          )

          return file.path ? (
            <button
              key={index}
              type="button"
              title={name}
              aria-label={`Preview ${name}`}
              onClick={() => setOpenIndex(index)}
              className="group w-20 text-left focus:outline-none"
            >
              <ArtworkThumb
                file={file}
                path={file.path}
                className="size-20 transition-colors group-hover:border-primary/60 group-focus-visible:ring-2 group-focus-visible:ring-primary/30"
              >
                {number}
              </ArtworkThumb>
              <span className="mt-1 block truncate text-2xs text-muted group-hover:text-brand-600">{name}</span>
            </button>
          ) : (
            <div key={index} title="File not uploaded" className="w-20">
              <span className="relative flex size-20 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-line text-center text-2xs text-muted">
                <FileX className="size-5" />
                Not uploaded
                {number}
              </span>
              <span className="mt-1 block truncate text-2xs text-muted">{name}</span>
            </div>
          )
        })}
      </div>
      <ArtworkViewer files={files} index={openIndex} onIndex={setOpenIndex} />
    </>
  )
}

/** Page 1 of a PDF at `width`, through the shared cache; null while loading. */
function usePdfPreview(path: string | null, enabled: boolean, width: number) {
  const [state, setState] = useState<{ path: string; preview?: PdfPreview; failed?: boolean } | null>(null)

  useEffect(() => {
    if (!enabled || !path) return
    let cancelled = false
    renderPdfFirstPage(path, width)
      .then((preview) => {
        if (!cancelled) setState({ path, preview })
      })
      .catch((error) => {
        console.error('artwork PDF preview failed', error)
        if (!cancelled) setState({ path, failed: true })
      })
    return () => {
      cancelled = true
    }
  }, [path, enabled, width])

  // A state left from the previous file (the viewer's prev / next) is ignored.
  const current = state?.path === path ? state : null
  return { preview: current?.preview ?? null, failed: current?.failed ?? false }
}

/**
 * One file as a square picture with its type in the corner (JPG, PDF, AI).
 * `className` sets the size; `children` overlay it (the gallery's number).
 */
export function ArtworkThumb({
  file,
  path,
  className,
  children,
}: {
  file: OrderItemArtwork
  path: string
  className: string
  children?: ReactNode
}) {
  return (
    <span className={cn('relative block shrink-0 overflow-hidden rounded-lg border border-line bg-sheet', className)}>
      <Thumb file={file} path={path} />
      {children}
      <span className="absolute bottom-1 right-1 rounded bg-background/90 px-1 text-[10px] font-bold leading-4 text-foreground shadow-sm">
        {artworkExt(file)}
      </span>
    </span>
  )
}

function Thumb({ file, path }: { file: OrderItemArtwork; path: string }) {
  const kind = artworkKind(file.mime, file.name, path)
  const pdf = usePdfPreview(path, kind === 'pdf', 320)
  const [broken, setBroken] = useState(false)

  if (kind === 'image' && !broken) {
    return <img src={path} alt="" loading="lazy" onError={() => setBroken(true)} className="size-full object-cover" />
  }
  if (kind === 'pdf' && !pdf.failed) {
    return pdf.preview ? (
      <img src={pdf.preview.dataUrl} alt="" className="size-full bg-white object-cover object-top" />
    ) : (
      <span className="block size-full animate-pulse bg-foreground/5" />
    )
  }
  return (
    <span className="flex size-full items-center justify-center text-muted">
      <FileText className="size-7" strokeWidth={1.5} />
    </span>
  )
}

/**
 * The preview dialog: the file large, its details, and the original to
 * download. `index` is the open file, null when closed.
 */
export function ArtworkViewer({
  files,
  index,
  onIndex,
}: {
  files: OrderItemArtwork[]
  index: number | null
  onIndex: (index: number | null) => void
}) {
  // Only uploaded files can be stepped through; a name-only entry has nothing to show.
  const viewable = files.flatMap((file, i) => (file.path ? [i] : []))
  const position = index == null ? -1 : viewable.indexOf(index)
  const file = index == null ? null : files[index]
  const step = (by: number) => {
    if (position < 0 || viewable.length < 2) return
    onIndex(viewable[(position + by + viewable.length) % viewable.length])
  }

  useEffect(() => {
    if (index == null || viewable.length < 2) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') step(-1)
      if (e.key === 'ArrowRight') step(1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  return (
    <Modal
      open={file != null && file.path != null}
      onClose={() => onIndex(null)}
      width="max-w-3xl"
      title={<span className="block [overflow-wrap:anywhere]">{file?.name ?? 'Artwork file'}</span>}
      footer={file?.path ? <ViewerActions file={file} path={file.path} /> : null}
    >
      {file?.path && (
        <ViewerBody
          file={file}
          path={file.path}
          // Numbered like the tiles, so "2 of 3" is the tile marked 2.
          count={files.length > 1 ? `${index! + 1} of ${files.length}` : null}
          onStep={viewable.length > 1 ? step : null}
        />
      )}
    </Modal>
  )
}

function ViewerBody({
  file,
  path,
  count,
  onStep,
}: {
  file: OrderItemArtwork
  path: string
  count: string | null
  onStep: ((by: number) => void) | null
}) {
  const kind = artworkKind(file.mime, file.name, path)
  const pdf = usePdfPreview(path, kind === 'pdf', 1400)
  const [brokenPath, setBrokenPath] = useState<string | null>(null)
  const ext = artworkExt(file)

  let preview
  if (kind === 'image' && brokenPath !== path) {
    preview = (
      <img
        key={path}
        src={path}
        alt={file.name ?? 'Customer artwork'}
        onError={() => setBrokenPath(path)}
        className="max-h-[65vh] w-auto max-w-full object-contain"
      />
    )
  } else if (kind === 'pdf' && !pdf.failed) {
    preview = pdf.preview ? (
      <img src={pdf.preview.dataUrl} alt={file.name ?? 'Customer artwork'} className="max-h-[65vh] w-auto max-w-full bg-white object-contain shadow-sm" />
    ) : (
      <p className="text-sm text-muted">Loading preview...</p>
    )
  } else {
    preview = (
      <div className="flex max-w-xs flex-col items-center gap-2 rounded-lg bg-background/90 p-5 text-center">
        <FileText className="size-10 text-muted" strokeWidth={1.5} />
        <p className="text-sm font-semibold text-ink">{kind === 'other' ? `No preview for ${ext} files` : 'The preview could not load'}</p>
        <p className="text-xs text-muted">Download the original to open it{kind === 'other' ? ' in Illustrator' : ''}.</p>
      </div>
    )
  }

  const meta = [
    ext,
    fileSize(file.size),
    pdf.preview ? `Page 1 of ${pdf.preview.pages}` : null,
  ].filter(Boolean)

  return (
    <>
      <div style={CHECKER} className="relative flex min-h-64 items-center justify-center overflow-hidden rounded-lg border border-line p-3">
        {preview}
        {onStep && (
          <>
            <StepButton side="left" onClick={() => onStep(-1)} />
            <StepButton side="right" onClick={() => onStep(1)} />
          </>
        )}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted">
        <span>{meta.join(' · ')}</span>
        {count && <span className="ml-auto font-semibold text-ink">{count}</span>}
      </div>
    </>
  )
}

function StepButton({ side, onClick }: { side: 'left' | 'right'; onClick: () => void }) {
  const Icon = side === 'left' ? ChevronLeft : ChevronRight
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={side === 'left' ? 'Previous file' : 'Next file'}
      className={
        'absolute top-1/2 flex size-10 -translate-y-1/2 items-center justify-center rounded-full border border-line bg-background/90 text-ink shadow-sm hover:bg-background ' +
        (side === 'left' ? 'left-2' : 'right-2')
      }
    >
      <Icon className="size-5" />
    </button>
  )
}

function ViewerActions({ file, path }: { file: OrderItemArtwork; path: string }) {
  const [downloading, setDownloading] = useState(false)

  return (
    <>
      <Button variant="outline" onClick={() => window.open(path, '_blank', 'noopener')}>
        <ExternalLink className="size-4" />
        Open in new tab
      </Button>
      <Button
        loading={downloading}
        onClick={async () => {
          setDownloading(true)
          try {
            await downloadArtwork({ name: file.name, path })
          } finally {
            setDownloading(false)
          }
        }}
      >
        {!downloading && <Download className="size-4" />}
        {downloading ? 'Downloading...' : 'Download original'}
      </Button>
    </>
  )
}
